import { useQuery } from '@tanstack/react-query';
import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import {
  BarChart3,
  Clapperboard,
  FileText,
  Home,
  Images,
  PenLine,
  Settings,
  Upload,
} from 'lucide-react';

import type { SidebarNavGroup } from '@/components/layout/Sidebar';

import { AppShell } from '@/components/layout/AppShell';
import { hasAnyCohostMembership } from '@/features/live/showActions';
import { useRequireAuth } from '@/lib/auth/useRequireAuth';
import { crossSurfaceLinks } from '@/lib/nav/roleNav';

export const Route = createFileRoute('/creator')({
  component: CreatorLayout,
});

function CreatorLayout() {
  const { loading, profile } = useRequireAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The production zone (studio + shows management) renders on the dark video
  // surface; the rest of the creator app stays editorial.
  const surface =
    pathname.startsWith('/creator/studio') || pathname.startsWith('/creator/shows')
      ? 'video'
      : 'editorial';
  // Un-gate the Studio for invited non-hosts: a user with any show membership
  // (pending or approved) needs the workspace to approve invites + go live.
  const memberId = profile?.id ?? '';
  const cohostMembership = useQuery({
    queryKey: ['my-cohost-membership', memberId],
    enabled: Boolean(memberId),
    queryFn: () => hasAnyCohostMembership(memberId),
  });
  if (loading) return null;
  const isApproved = profile?.creator_status === 'approved';
  const isAdmin = profile?.role === 'admin';
  const isCohost = cohostMembership.data === true;

  // Phase F: all creator tools are live. Items only stay disabled for
  // not-yet-approved creators (gated on approval, not on phase).
  const needsApproval = isApproved ? undefined : 'Approval required';
  const sidebarGroups: SidebarNavGroup[] = [
    {
      items: [
        { to: '/creator', label: 'Overview', icon: Home },
        {
          to: '/creator/studio',
          label: 'Streaming',
          icon: Clapperboard,
          disabled: !isApproved && !isAdmin && !isCohost,
          disabledTooltip: needsApproval,
        },
      ],
    },
    {
      label: 'Content',
      items: [
        {
          to: '/creator/content',
          label: 'My content',
          icon: FileText,
          disabled: !isApproved && !isAdmin,
          disabledTooltip: needsApproval,
        },
        {
          to: '/creator/write',
          label: 'Write',
          icon: PenLine,
          disabled: !isApproved && !isAdmin,
          disabledTooltip: needsApproval,
        },
        {
          to: '/creator/upload',
          label: 'Upload',
          icon: Upload,
          disabled: !isApproved && !isAdmin,
          disabledTooltip: needsApproval,
        },
        {
          to: '/creator/assets',
          label: 'Images',
          icon: Images,
          disabled: !isApproved && !isAdmin,
          disabledTooltip: needsApproval,
        },
      ],
    },
    {
      label: 'Insights',
      items: [
        {
          to: '/creator/analytics',
          label: 'Analytics',
          icon: BarChart3,
          disabled: !isApproved && !isAdmin,
          disabledTooltip: needsApproval,
        },
      ],
    },
    {
      items: [{ to: '/creator/settings', label: 'Settings', icon: Settings }],
    },
  ];

  // Role-aware chrome (Spec 27): the cross-surface "Manage" group.
  const cross = crossSurfaceLinks(profile, 'creator');
  if (cross.length) sidebarGroups.push({ label: 'Manage', items: cross });

  const bottomNavItems = [
    { to: '/creator', label: 'Overview', icon: Home },
    { to: '/creator/content', label: 'Content', icon: FileText },
    { to: '/creator/write', label: 'Write', icon: PenLine },
    { to: '/creator/analytics', label: 'Stats', icon: BarChart3 },
  ];

  return (
    <AppShell
      pageTitle="Contributor desk"
      sidebarGroups={sidebarGroups}
      bottomNavItems={bottomNavItems}
      homeHref="/creator"
      exitLink={{ to: '/subscriber', label: 'Home' }}
      surface={surface}
    >
      <Outlet />
    </AppShell>
  );
}
